import { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Descriptions, Tabs, Table, Space, Alert, Popconfirm } from 'antd';
import { startRegistration } from '@simplewebauthn/browser';
import { useAuthStore } from '../store/auth';
import {
  updateMyProfile,
  getMe,
  getMyPasskeys,
  getBindPasskeyOptions,
  bindPasskey,
  unbindPasskey,
  changePassword,
} from '../lib/api';
import type { PasskeyInfo } from '../lib/api';
import { SafetyCertificateOutlined, KeyOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

export default function Profile() {
  const { user, setUserData } = useAuthStore();
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [passkeys, setPasskeys] = useState<PasskeyInfo[]>([]);
  const [passkeysLoading, setPasskeysLoading] = useState(false);
  const [bindingPasskey, setBindingPasskey] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'boss':
        return '老闆';
      case 'manager':
        return '主管';
      case 'employee':
        return '員工';
      default:
        return role || '';
    }
  };

  useEffect(() => {
    loadUser();
    loadPasskeys();
  }, []);

  const loadUser = async () => {
    setLoading(true);
    try {
      const freshUser = await getMe();
      setUserData(freshUser);
      profileForm.setFieldsValue({
        name: freshUser.name,
        email: freshUser.email,
        username: freshUser.username,
      });
    } catch (error) {
      message.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadPasskeys = async () => {
    setPasskeysLoading(true);
    try {
      const data = await getMyPasskeys();
      setPasskeys(data);
    } catch (error) {
      console.error('Failed to load passkeys:', error);
    } finally {
      setPasskeysLoading(false);
    }
  };

  const handleSubmit = async (values: { name?: string; email?: string; username?: string }) => {
    setSaving(true);
    try {
      await updateMyProfile(values);
      message.success('Profile updated successfully');
      const freshUser = await getMe();
      setUserData(freshUser);
    } catch (error: any) {
      message.error(error.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleBindPasskey = async () => {
    setBindingPasskey(true);
    try {
      const options = await getBindPasskeyOptions();
      const challengeId = options.challengeId;
      const { challengeId: _, ...webauthnOptions } = options;

      const attestationResponse = await startRegistration(webauthnOptions);

      await bindPasskey({
        challengeId,
        response: attestationResponse,
        deviceName: navigator.platform || 'Unknown Device',
      });

      message.success('Passkey 綁定成功');
      loadPasskeys();
    } catch (err: any) {
      message.error(err.message || 'Passkey 綁定失敗');
    } finally {
      setBindingPasskey(false);
    }
  };

  const handleUnbindPasskey = async (credentialId: string) => {
    try {
      await unbindPasskey(credentialId);
      message.success('Passkey 已解除綁定');
      loadPasskeys();
    } catch (err: any) {
      message.error(err.message || '解除綁定失敗');
    }
  };

  const handleChangePassword = async (values: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('新密碼與確認密碼不一致');
      return;
    }

    // Validate password strength
    if (values.newPassword.length < 8) {
      message.error('新密碼必須至少 8 個字符');
      return;
    }
    if (!/[A-Z]/.test(values.newPassword)) {
      message.error('新密碼必須包含至少一個大寫英文字母');
      return;
    }
    if (!/[a-z]/.test(values.newPassword)) {
      message.error('新密碼必須包含至少一個小寫英文字母');
      return;
    }
    if (!/[0-9]/.test(values.newPassword)) {
      message.error('新密碼必須包含至少一個數字');
      return;
    }

    setChangingPassword(true);
    try {
      await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      message.success('密碼變更成功');
      passwordForm.resetFields();
    } catch (err: any) {
      message.error(err.message || '密碼變更失敗');
    } finally {
      setChangingPassword(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '從未使用';
    return new Date(dateStr).toLocaleString('zh-TW');
  };

  const passkeyColumns = [
    {
      title: '裝置名稱',
      dataIndex: 'deviceName',
      render: (name: string | null) => name || '未命名裝置',
    },
    {
      title: '綁定時間',
      dataIndex: 'createdAt',
      render: formatDate,
    },
    {
      title: '最後使用',
      dataIndex: 'lastUsedAt',
      render: formatDate,
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: PasskeyInfo) => (
        <Popconfirm
          title="解除綁定"
          description="確定要解除這個 Passkey 的綁定嗎？解除後將無法使用此裝置快速登入。"
          onConfirm={() => handleUnbindPasskey(record.credentialId)}
          okText="確定"
          cancelText="取消"
        >
          <Button danger icon={<DeleteOutlined />} size="small">
            解除綁定
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const profileContent = (
    <>
      <Descriptions bordered column={1} style={{ marginBottom: 24 }}>
        <Descriptions.Item label="ID">{user?.id}</Descriptions.Item>
        <Descriptions.Item label="角色">{getRoleLabel(user?.role)}</Descriptions.Item>
      </Descriptions>

      <Form
        form={profileForm}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          name: user?.name,
          email: user?.email,
          username: user?.username,
        }}
      >
        <Form.Item
          name="name"
          label="姓名"
          rules={[{ max: 100, message: 'Name too long' }]}
        >
          <Input placeholder="您的姓名" />
        </Form.Item>

        <Form.Item
          name="email"
          label="電子郵件"
          rules={[{ type: 'email', message: 'Invalid email format' }]}
        >
          <Input placeholder="user@example.com" />
        </Form.Item>

        <Form.Item
          name="username"
          label="帳號名"
          rules={[
            {
              pattern: /^[a-zA-Z0-9_-]+$/,
              message: 'Username can only contain letters, numbers, underscores, and hyphens',
            },
          ]}
        >
          <Input placeholder="英文數字底線連字符" />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>
            儲存變更
          </Button>
        </Form.Item>
      </Form>
    </>
  );

  const passwordContent = (
    <>
      <Alert
        title="密碼要求"
        description={
          <ul style={{ paddingLeft: 20, margin: '4px 0' }}>
            <li>至少 8 個字符</li>
            <li>包含大寫字母 (A-Z)</li>
            <li>包含小寫字母 (a-z)</li>
            <li>包含數字 (0-9)</li>
          </ul>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form
        form={passwordForm}
        layout="vertical"
        onFinish={handleChangePassword}
        style={{ maxWidth: 400 }}
      >
        <Form.Item
          name="currentPassword"
          label="目前密碼（如未設置過密碼可留空）"
        >
          <Input.Password placeholder="目前密碼" autoComplete="current-password" />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label="新密碼"
          rules={[{ required: true, message: '請輸入新密碼' }]}
        >
          <Input.Password placeholder="新密碼" autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="確認新密碼"
          rules={[
            { required: true, message: '請再次輸入新密碼' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('兩次輸入的密碼不一致'));
              },
            }),
          ]}
        >
          <Input.Password placeholder="確認新密碼" autoComplete="new-password" />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={changingPassword}>
            變更密碼
          </Button>
        </Form.Item>
      </Form>
    </>
  );

  const passkeyContent = (
    <>
      <Alert
        title="Passkey 是什麼？"
        description="Passkey 是一種更安全的登入方式，使用您的裝置指紋、臉部辨識或 PIN 碼來驗證身份，無需輸入密碼。"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Space orientation="vertical" style={{ width: '100%' }} size="middle">
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleBindPasskey}
          loading={bindingPasskey}
        >
          綁定新 Passkey
        </Button>

        <Table
          dataSource={passkeys}
          columns={passkeyColumns}
          rowKey="id"
          loading={passkeysLoading}
          pagination={false}
          locale={{ emptyText: '尚未綁定任何 Passkey' }}
        />
      </Space>
    </>
  );

  const tabItems = [
    {
      key: 'profile',
      label: '基本資料',
      icon: <KeyOutlined />,
      children: profileContent,
    },
    {
      key: 'password',
      label: '變更密碼',
      icon: <KeyOutlined />,
      children: passwordContent,
    },
    {
      key: 'passkeys',
      label: 'Passkey 管理',
      icon: <SafetyCertificateOutlined />,
      children: passkeyContent,
    },
  ];

  return (
    <Card title="個人設定" loading={loading}>
      <Tabs items={tabItems} />
    </Card>
  );
}
